import React, { useEffect, useState } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { useUser } from '@clerk/clerk-react';
import { clerkEnabled } from '../auth/clerk';
import { getCurrentUser } from '../utils';
import type { Perspective } from '../types';
import useVoyagerStore from '../stores/useVoyagerStore';
import useStudioStore from '../stores/useStudioStore';
import ChatComposer from '../components/LandingAgent/ChatComposer';
import { fillDraft } from '../components/LandingAgent/composerDrafts';
import type { StatusTone } from '../design/primitives';
import { metaSx, panelHeadSx, panelSx } from '../components/Studio/studioPresentation';
import type { AgentRow, Progress } from '../components/Agents/api';
import { readHomeQueue, readTrending, type HomeQueueData, type TrendingMetric } from './homeApi';
import BuildCards from '../components/Builds/BuildCards';

/**
 * Home (#569), now the chat (#765): the New-chat screen. A greeting and a large
 * centred composer, the first-run checklist until a model is published, then Trending (#1504), or a
 * few suggestions on a first visit (#2207). The helpers the objective pages share live here too.
 */

const FOCUS = { '&.Mui-focusVisible, &:focus-visible': { outline: '2px solid var(--accent)', outlineOffset: '-2px' } } as const;

function Panel({ title, meta, action, children, testId }: React.PropsWithChildren<{ title: string; meta?: React.ReactNode; action?: React.ReactNode; testId: string }>) {
  return <Box component="section" sx={panelSx} data-testid={testId} aria-label={title}>
    <Box sx={panelHeadSx}>
      <h3>{title}</h3>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        {meta != null && <Box sx={metaSx}>{meta}</Box>}
        {action}
      </Box>
    </Box>
    {children}
  </Box>;
}

const initials = (name: string | null | undefined) => {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  return (words.length > 1 ? words[0][0] + words[1][0] : (words[0] || '?').slice(0, 2)).toUpperCase();
};

/** A person is a circle; an agent is a square in the agent tint; Stroma itself is ink. */
export function Avatar({ name, kind }: { name: string | null | undefined; kind: 'agent' | 'person' | 'system' }) {
  const label = kind === 'system' ? 'Stroma' : name || (kind === 'person' ? 'You' : 'An agent');
  const sx = kind === 'agent'
    ? { borderRadius: '6px', bgcolor: 'var(--agent-tint)', color: 'var(--agent)', border: '1px solid var(--agent)' }
    : kind === 'system' ? { borderRadius: '50%', bgcolor: 'var(--ink)', color: 'var(--surface)' }
      : { borderRadius: '50%', bgcolor: 'var(--accent)', color: 'var(--on-accent)' };
  return <Box component="span" role="img" aria-label={label} title={label}
    sx={{ width: 24, height: 24, flex: 'none', display: 'inline-grid', placeItems: 'center', fontSize: 10, fontWeight: 700, ...sx }}>
    {kind === 'system' ? 'S' : initials(label)}
  </Box>;
}

const fmt = (v: number | null | undefined) => (v == null || !Number.isFinite(v) ? '—'
  : Math.abs(v) >= 1000 ? Math.round(v).toLocaleString() : Number(v.toFixed(Math.abs(v) < 10 ? 2 : 1)).toLocaleString());

/** The objective's target as a value: an absolute target, or the baseline moved by the percentage. */
export function targetValue(p: Pick<Progress, 'target' | 'baseline'> | null | undefined, fallback?: AgentRow['objective']['target']): string {
  const t = p?.target || fallback;
  if (!t) return '—';
  if (t.kind === 'abs') return fmt(t.value);
  if (p?.baseline != null) return fmt(p.baseline * (1 + t.value / 100));
  return `${t.value > 0 ? '+' : ''}${t.value}%`;
}

/**
 * Where the track draws a point: attainment on a scale from a quarter below
 * the baseline to the target, so the baseline mark sits inside the track and
 * a number that went backwards reads as left of it.
 */
export const trackAt = (attainment: number) => (Math.max(-0.25, Math.min(1, attainment)) + 0.25) / 1.25;

export function objectiveStatus(agent: AgentRow): { tone: StatusTone; label: string } {
  const run = agent.last_run;
  if (agent.state === 'paused') return { tone: 'off', label: 'Paused' };
  if (!run) return { tone: 'draft', label: 'First run pending' };
  if (run.status === 'suppressed') return { tone: 'stop', label: 'Guardrail held' };
  if (run.status === 'denied' || run.status === 'failed') return { tone: 'stop', label: 'Needs attention' };
  const onPace = (run.target as Progress)?.on_pace;
  if (onPace === true) return { tone: 'running', label: 'On pace' };
  if (onPace === false) return { tone: 'draft', label: 'Behind pace' };
  return { tone: 'running', label: 'Running' };
}

/**
 * The first run (#1504): warehouse → sources → model, each checked by the server; the checklist leaves Home once a
 * model is published.
 */
export const SIMPLE_FIRST_RUN = [
  { key: 'warehouse', hint: 'Connect your warehouse', detail: 'Stroma reads your numbers where they live.' },
  { key: 'sources', hint: 'Connect the sources Stroma learns from', detail: 'Slack, docs and meetings: the context around each number.' },
  { key: 'model', hint: 'Publish a semantic model', detail: 'One definition per metric, used every time.' },
] as const;

function stepDone(data: HomeQueueData, key: string): boolean {
  return !!data.first_run.steps.find((s) => s.key === key)?.done;
}

function goStep(key: string) {
  const s = useVoyagerStore.getState();
  if (key === 'warehouse' || key === 'sources') s.navigateToConnections();
  if (key === 'model') { useStudioStore.getState().setStudioHome('perspectives'); s.navigateToStudio(); }
}

export const simpleFirstRunInPlace = (data: HomeQueueData) => !stepDone(data, 'model');

function FirstRun({ data }: { data: HomeQueueData }) {
  const steps = SIMPLE_FIRST_RUN;
  const done = steps.filter((step) => stepDone(data, step.key)).length;
  if (done === steps.length) return null;
  return <Panel title="Get Stroma ready" meta={`${done} of ${steps.length} done`} testId="home-first-run">
    {data.first_run.note && <Typography role="status" sx={{ ...metaSx, px: 2, pt: 1.25 }}>{data.first_run.note}</Typography>}
    {steps.map((step, i) => {
      const ok = stepDone(data, step.key);
      return <ButtonBase key={step.key} data-testid={`setup-${step.key}`} data-done={ok} onClick={() => goStep(step.key)}
        sx={{ display: 'grid', gridTemplateColumns: 'auto minmax(0,1fr)', gap: 1.5, alignItems: 'center', width: '100%', textAlign: 'left', px: 2, py: 1.25, borderTop: i ? '1px solid var(--line-soft)' : 0, '&:hover': { bgcolor: 'var(--sunk)' }, ...FOCUS }}>
        <Box sx={{ width: 24, height: 24, borderRadius: '50%', display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 700,
          ...(ok ? { bgcolor: 'var(--ok-tint)', color: 'var(--ok)' } : { border: '1px solid var(--line)', color: 'var(--ink-3)' }) }}>
          {ok ? <CheckIcon sx={{ fontSize: 15 }} aria-label="done" /> : i + 1}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: ok ? 400 : 700, color: ok ? 'var(--ink-3)' : 'var(--ink)', textDecoration: ok ? 'line-through' : 'none' }}>{step.hint}</Typography>
          {!ok && <Typography sx={metaSx}>{step.detail}</Typography>}
        </Box>
      </ButtonBase>;
    })}
  </Panel>;
}

/** The question a Trending row puts in the composer: about that metric, in words the router reads as a data question. */
export const trendingQuestion = (name: string) => `How did ${name} do last week?`;

/** A metric's name as words: `avg_delivery_time` → "Avg delivery time". */
const metricWords = (id: string) => {
  const words = id.replace(/[_\s]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
};

/**
 * The first-visit suggestions (#2207), from the asker's own catalog: what they can ask about, then a How about the
 * first key metric and a Why about the next (the first again when there is only one). No key metric, no suggestions.
 */
export function starterQuestions(perspectives: ReadonlyArray<Pick<Perspective, 'keyMetrics'>>): string[] {
  const metrics = perspectives.flatMap((p) => (Array.isArray(p.keyMetrics) ? p.keyMetrics : [])
    .filter((m): m is string => typeof m === 'string' && !!metricWords(m)));
  if (!metrics.length) return [];
  const why = metrics.find((m) => m !== metrics[0]) ?? metrics[0];
  return ['What can I ask about?', `How did ${metricWords(metrics[0])} do last week?`, `Why did ${metricWords(why)} change last week?`];
}

/** Home's list of starting points: a quiet heading, then rows that fill the composer. */
function QuestionList({ testId, title, rowTestId, rows, icon, onPick }: {
  testId: string; title: string; rowTestId: string; icon: React.ReactNode;
  rows: Array<{ key: string; metric: string; label: string; question: string }>; onPick: (question: string) => void;
}) {
  return <Box component="section" aria-labelledby={`${testId}-title`} data-testid={testId} sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
    <Typography component="h2" id={`${testId}-title`} sx={{ fontSize: 12, fontWeight: 500, color: 'var(--ink-3)', px: 1.5, pb: 0.75, m: 0 }}>
      {title}
    </Typography>
    <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {rows.map((r, i) => <Box component="li" key={r.key} sx={{ borderTop: i ? '1px solid var(--line-2)' : 0, '&:hover, &:hover + li': { borderTopColor: 'transparent' } }}>
        <ButtonBase data-testid={rowTestId} data-metric={r.metric} onClick={() => onPick(r.question)}
          sx={{ display: 'grid', gridTemplateColumns: '16px minmax(0,1fr)', gap: 1.5, alignItems: 'center', width: '100%', textAlign: 'left', px: 1.5, py: 1.375, borderRadius: '10px', fontSize: 14, color: 'var(--ink-2)', '&:hover': { bgcolor: 'var(--hover)', color: 'var(--ink)' }, '& > svg': { fontSize: 16, color: 'var(--ink-3)' }, ...FOCUS }}>
          {icon}
          <Box component="span" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.label}</Box>
        </ButtonBase>
      </Box>)}
    </Box>
  </Box>;
}

/**
 * Trending in your workspace this week (#1504): the metrics the server released, in its rank order, with no
 * numbers (owner decision on #1500). Nothing renders while the list is empty, still loading or unreadable.
 */
function Trending({ metrics, onPick }: { metrics: TrendingMetric[]; onPick: (question: string) => void }) {
  if (!metrics.length) return null;
  return <QuestionList testId="home-trending" title="Trending in your workspace this week" rowTestId="trending-row" icon={<ShowChartIcon aria-hidden />}
    rows={metrics.map((m, i) => ({ key: `${m.id}:${i}`, metric: m.id, label: m.name, question: trendingQuestion(m.name) }))} onPick={onPick} />;
}

/**
 * The perspectives a question sent from Home is answered over, as the composer sends it: a picked perspective is
 * that one alone; Auto is what the server's Auto plans over (`fetch_auto_catalog`: every active perspective, only
 * the picked domain's when the domain layer is on); no pick sends nothing, so it offers nothing.
 */
export function askableScope<P extends Pick<Perspective, 'id' | 'domain' | 'status'>>(
  perspectives: ReadonlyArray<P>, pick: string | null, domainId: string | null, domainsOn: boolean,
): P[] {
  if (!pick) return [];
  if (pick !== 'auto') return perspectives.filter((p) => p.id === pick);
  return perspectives.filter((p) => (p.status ?? 'active') === 'active' && (!domainsOn || !domainId || p.domain === domainId));
}

export const HOME_PLACEHOLDER = 'Ask about your numbers, or what your team has decided';

/** Suggestions on a first visit: quiet buttons under the composer that fill it, never send. */
function FirstVisitSuggestions({ questions, onPick }: { questions: string[]; onPick: (question: string) => void }) {
  return <Box data-testid="home-suggestions" sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1 }}>
    {questions.map((q) => <ButtonBase key={q} data-testid="suggestion" onClick={() => onPick(q)}
      sx={{ minHeight: 36, px: 1.75, borderRadius: '9px', border: '1px solid var(--line)', bgcolor: 'var(--surface)', fontSize: 13.5, color: 'var(--ink-2)', '&:hover': { bgcolor: 'var(--hover)', color: 'var(--ink)' }, ...FOCUS }}>{q}</ButtonBase>)}
  </Box>;
}

const greeting = (now: Date) => (now.getHours() < 12 ? 'Good morning' : now.getHours() < 18 ? 'Good afternoon' : 'Good evening');

/** The first word of a name, or null. */
const firstWord = (name: string | null | undefined) => (name || '').trim().split(/\s+/)[0] || null;

/** The greeting: the Stroma mark beside it and the person's first name when it is known. */
function Greeting({ now, welcome }: { now: Date; welcome: boolean }) {
  return clerkEnabled() ? <ClerkGreeting now={now} welcome={welcome} /> : <GreetingView now={now} welcome={welcome} name={firstWord(getCurrentUser())} />;
}

/** Clerk's hooks need its provider, which is mounted only when Clerk is on. */
function ClerkGreeting({ now, welcome }: { now: Date; welcome: boolean }) {
  const { user } = useUser();
  return <GreetingView now={now} welcome={welcome} name={firstWord(user?.firstName) || firstWord(user?.fullName)} />;
}

function GreetingView({ now, welcome, name }: { now: Date; welcome: boolean; name: string | null }) {
  const hello = welcome ? 'Welcome to Stroma' : greeting(now);
  return <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 1.25, sm: 1.75 } }}>
    {/* the Stroma mark (the sidebar's, the mock's) */}
    <Box component="img" data-testid="home-brand-mark" src={`${process.env.PUBLIC_URL}/stroma-mark.svg`} alt="" sx={{ width: { xs: 30, sm: 36 }, height: 'auto', display: 'block', flex: 'none' }} />
    <Typography component="h1" sx={{ fontFamily: 'var(--ui)', fontSize: { xs: 28, sm: 34 }, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.2, m: 0, minWidth: 0 }}>
      {name ? `${hello}, ${name}` : hello}
    </Typography>
  </Box>;
}

/** One scoped queue read paints Home; a workspace switch discards what was in flight. */
export default function StromaHome() {
  const generation = useVoyagerStore((s) => `${s.agentsGeneration}:${s.connectionsGeneration}`);
  return <Home key={generation} />;
}

/**
 * Home is the chat (#765): the screen you land on, and New question / ⌘K /
 * the sidebar's Home. A greeting, a large centred composer, the first-run
 * checklist until a model is published, then Trending (#1504), or on a first
 * visit a few suggestions (#2207). The first question turns it into a thread with the docked composer.
 */
function Home() {
  const [data, setData] = useState<HomeQueueData | null>(null);
  // an unreadable list is an empty one
  const [trending, setTrending] = useState<TrendingMetric[]>([]);
  useEffect(() => {
    let live = true;
    readTrending().then((metrics) => { if (live) setTrending(metrics); }, () => {});
    return () => { live = false; };
  }, []);
  useEffect(() => {
    let live = true;
    readHomeQueue().then((queue) => { if (live) setData(queue); }, () => {});
    useVoyagerStore.getState().fetchAgents().catch(() => {});
    return () => { live = false; };
  }, []);
  // disabled only where we POSITIVELY know a question cannot be answered: no published model
  const model = data?.first_run.steps.find((s) => s.key === 'model');
  const ready = !model || model.done;
  const now = new Date();
  const builder = useVoyagerStore((s) => !!s.features?.agent_builder);
  const perspectives = useVoyagerStore((s) => s.perspectivesList);
  const pick = useVoyagerStore((s) => s.selectedPerspectiveId);
  const domainId = useVoyagerStore((s) => s.selectedLandingAgentDomainId);
  const domainsOn = useVoyagerStore((s) => s.features.domains === true);
  // a first visit: their threads were read and there are none, and a question can be answered
  const firstVisit = useVoyagerStore((s) => s.threadsLoaded && s.savedThreads.length === 0) && ready;
  // only what the question will be sent over, so a suggestion never names a metric outside the composer's pick
  const suggestions = React.useMemo(() => (firstVisit ? starterQuestions(askableScope(perspectives || [], pick, domainId, domainsOn)) : []),
    [firstVisit, perspectives, pick, domainId, domainsOn]);
  const welcome = suggestions.length > 0;
  return <Box data-testid="home-view" sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: 'var(--ground)', position: 'relative' }}>
    <Box data-testid="new-chat" sx={{ flex: 1, overflow: 'auto', px: { xs: 2, md: 3.5 } }}>
      <Box sx={{ maxWidth: 760, mx: 'auto', pt: '11vh', pb: 6, display: 'flex', flexDirection: 'column', gap: 3.25 }}>
        <Greeting now={now} welcome={welcome} />
        <ChatComposer variant="hero" draftKey="home" placeholder={HOME_PLACEHOLDER} disabledReason={ready ? undefined : 'Publish a semantic model to ask your first question'} />
        {/* #1625 (owner, #1470): a card per live build of the viewer's, with AGENT_BUILDER on */}
        {builder && <BuildCards />}
        {welcome && <FirstVisitSuggestions questions={suggestions} onPick={(q) => fillDraft('home', q)} />}
        {/* #1504: the first-run checklist until a model is published */}
        {data && simpleFirstRunInPlace(data) && <FirstRun data={data} />}
        {/* the suggestions take its place on a first visit */}
        {!welcome && <Trending metrics={trending} onPick={(q) => fillDraft('home', q)} />}
      </Box>
    </Box>
  </Box>;
}
